/**
 * ErrorBoundary — bắt lỗi render ngoài phạm vi route (vd lỗi trong Provider tree). Class
 * component vì React chỉ hỗ trợ error boundary qua componentDidCatch/getDerivedStateFromError.
 * Dùng chung cho admin/landing/transparency. Nhận `fallback` tuỳ biến theo từng app.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";

export interface ErrorBoundaryProps {
  readonly children: ReactNode;
  readonly fallback?: (error: Error) => ReactNode;
  readonly onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  readonly error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error) {
      if (this.props.fallback) return this.props.fallback(error);
      return (
        <section className="gp-error-boundary" role="alert">
          <h1>Có lỗi xảy ra</h1>
          <p>{error.message}</p>
        </section>
      );
    }
    return this.props.children;
  }
}
